import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { MdWarning } from 'react-icons/md';
import PropTypes from 'prop-types';

import { Overlay, Caixa } from './styles';

/**
 * =====================================================================
 * CONFIRMAÇÃO DE EXCLUSÃO EM LOTE
 * ---------------------------------------------------------------------
 * A exclusão de item de página é irreversível e apaga registro de
 * ITEMPAGINA, por isso a confirmação exige digitar a palavra.
 *
 * Observação técnica: window.confirm() só oferece OK/Cancelar, não aceita
 * campo de digitação — quem aceita é o window.prompt(), que não permite
 * listar os itens nem bloquear o botão. Por isso a confirmação é um
 * diálogo próprio, montado em portal no body para não ser recortado pelo
 * overflow da coluna de itens nem no modo tela cheia.
 * =====================================================================
 */
const PALAVRA_CONFIRMACAO = 'EXCLUIR';

function ModalExcluirItens({ itens, comLink, excluindo, onCancel, onConfirm }) {
  const inputRef = useRef(null);
  const [texto, setTexto] = useState('');

  /** aceita minúsculas e espaços sobrando; a intenção continua explícita */
  const liberado =
    texto.trim().toUpperCase() === PALAVRA_CONFIRMACAO && !excluindo;

  useEffect(() => {
    if (inputRef.current) inputRef.current.focus();
  }, []);

  const handleConfirmar = useCallback(() => {
    if (!liberado) return;
    onConfirm();
  }, [liberado, onConfirm]);

  /**
   * O editor do desenho escuta teclas globais (Del, Esc, Alt+Z...). O
   * stopPropagation impede que a digitação aqui dispare qualquer atalho.
   */
  const handleKeyDown = useCallback(
    (e) => {
      e.stopPropagation();

      if (e.key === 'Enter') {
        e.preventDefault();
        handleConfirmar();
      }

      if (e.key === 'Escape') {
        e.preventDefault();
        if (!excluindo) onCancel();
      }
    },
    [handleConfirmar, onCancel, excluindo]
  );

  const total = itens.length;

  return createPortal(
    <Overlay
      role="presentation"
      onKeyDown={handleKeyDown}
      onMouseDown={(e) => {
        /** clique fora fecha, desde que a exclusão não esteja em curso */
        if (e.target === e.currentTarget && !excluindo) onCancel();
      }}
    >
      <Caixa
        role="dialog"
        aria-modal="true"
        aria-label="Confirmar exclusão de itens"
      >
        <Caixa.Header>
          <MdWarning size={22} />
          <strong>
            {total === 1
              ? 'Excluir 1 item da página'
              : `Excluir ${total} itens da página`}
          </strong>
        </Caixa.Header>

        <Caixa.Body>
          <p>
            Esta ação remove o item do cadastro da página e{' '}
            <b>não pode ser desfeita</b>. Confira a lista antes de confirmar.
          </p>

          <Caixa.Lista>
            {itens.map((item) => (
              <li key={`${item.idItem}-${item.idMaterial}`}>
                <span>{item.idItem}</span>
                <span>{item.partnumber}</span>
              </li>
            ))}
          </Caixa.Lista>

          {comLink > 0 && (
            <Caixa.Aviso>
              <MdWarning size={16} />
              <span>
                {comLink === 1
                  ? '1 item selecionado tem link no desenho.'
                  : `${comLink} itens selecionados têm link no desenho.`}{' '}
                O link não é apagado automaticamente: remova-o no desenho e
                salve a página, senão ele fica apontando para um item que não
                existe mais.
              </span>
            </Caixa.Aviso>
          )}

          <Caixa.Campo htmlFor="confirmaExclusaoItens">
            <span>
              Para confirmar, digite <b>{PALAVRA_CONFIRMACAO}</b>:
            </span>
            <input
              id="confirmaExclusaoItens"
              ref={inputRef}
              type="text"
              autoComplete="off"
              spellCheck="false"
              /**
               * Sem placeholder de propósito: com a própria palavra ali o
               * campo parece já preenchido.
               */
              value={texto}
              disabled={excluindo}
              onChange={(e) => setTexto(e.target.value)}
            />
          </Caixa.Campo>
        </Caixa.Body>

        <Caixa.Footer>
          <button type="button" onClick={onCancel} disabled={excluindo}>
            Cancelar
          </button>
          <button
            type="button"
            className="perigo"
            onClick={handleConfirmar}
            disabled={!liberado}
          >
            {excluindo ? 'Excluindo...' : 'Excluir definitivamente'}
          </button>
        </Caixa.Footer>
      </Caixa>
    </Overlay>,
    document.body
  );
}

export default ModalExcluirItens;

ModalExcluirItens.propTypes = {
  itens: PropTypes.arrayOf(PropTypes.instanceOf(Object)).isRequired,
  comLink: PropTypes.number,
  excluindo: PropTypes.bool,
  onCancel: PropTypes.func.isRequired,
  onConfirm: PropTypes.func.isRequired,
};

ModalExcluirItens.defaultProps = {
  comLink: 0,
  excluindo: false,
};
